import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useLocation } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import { FormProvider, useForm } from 'react-hook-form'
import { Header } from '../components/Header'
import { Loader } from '@refera/ui-web'
import {
  GeneralInfoAccordion,
  AddressAccordion,
  ValuesAccordion,
  RelatedBudgetsAccordion,
} from '../components/Accordions'
import { yupResolver } from '@hookform/resolvers/yup'
import { inspectionBudgetFormSchema } from '../utils/schemas'
import { getInspectionBudgetById } from '_/modules/inspectionBudget/actions'
import { getInspectionBudgetByIdSelector } from '_modules/inspectionBudget/selector'
import InspectionLoadingContext from '../hooks/use-inspection-loading'
import { useInspectionBudgetCatalogs } from '../hooks/use-inspection-budget-catalogs'

import useStyles from './styles'

export default function InspectionBudgetView({ budgetType = 'inspection' }) {
  const { budgetId } = useParams()
  const dispatch = useDispatch()
  const location = useLocation()
  const defaults = location?.state?.defaults
  const currentBudget = useSelector(getInspectionBudgetByIdSelector)
  const budget = budgetId ? currentBudget : null

  const [loading, setLoading] = useState(false)
  const [firstLoad, setFirstLoad] = useState(false)
  const [relatedBudgets, setRelatedBudgets] = useState([])
  const [isEditing, setIsEditing] = useState(!budgetId)
  const [initialFormValues, setInitialFormValues] = useState(null)

  const styles = useStyles()
  useInspectionBudgetCatalogs()

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(inspectionBudgetFormSchema(budgetType)),
  })

  useEffect(() => {
    setIsEditing(!budgetId)
  }, [budgetId])

  useEffect(() => {
    if (budgetId) {
      setLoading(true)
      setFirstLoad(true)
      dispatch(getInspectionBudgetById(budgetId))
        .then(res => {
          setRelatedBudgets(res?.relatedBudgets ?? [])
        })
        .finally(() => {
          setLoading(false)
          setFirstLoad(false)
        })
    }
  }, [budgetId])

  useEffect(() => {
    if (budgetId && budget?.id) {
      const formValues = structuredClone(budget)
      setInitialFormValues(formValues)
      methods.reset(formValues)
    } else if (!budgetId) {
      const inspectionPercentages = defaults?.agency?.inspectionPercentages || []
      const defaultValues = {
        ...defaults,
        percentualPriceRefera: inspectionPercentages[0]?.referaPercentage || 0,
        percentualPriceAgency: inspectionPercentages[0]?.agencyPercentage || 0,
        bitrixId: null,
        propertyCode: '',
        squareFootage: null,
      }
      const formValues = structuredClone(defaultValues)
      setInitialFormValues(formValues)
      methods.reset(defaultValues)
    }
  }, [budgetId, budget?.id, defaults])

  const contextValue = useMemo(
    () => ({
      budget,
      budgetType,
      loading,
      setLoading,
      relatedBudgets,
      setRelatedBudgets,
      isEditing,
      setIsEditing,
      initialFormValues,
      setInitialFormValues,
    }),
    [
      budget,
      budgetType,
      loading,
      setLoading,
      relatedBudgets,
      setRelatedBudgets,
      isEditing,
      setIsEditing,
      initialFormValues,
      setInitialFormValues,
    ]
  )

  if (firstLoad) {
    return <Loader hasBackdrop open />
  }

  return (
    <InspectionLoadingContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <form id="inspection-budget-form">
          <Header />
          <div className={styles.container}>
            <Loader hasBackdrop open={loading} />
            <AddressAccordion />
            <GeneralInfoAccordion />
            <ValuesAccordion />
            {relatedBudgets?.length > 0 && <RelatedBudgetsAccordion />}
          </div>
        </form>
      </FormProvider>
    </InspectionLoadingContext.Provider>
  )
}
